import { useState, useCallback, useMemo } from 'react';
import { 
    createFilterCondition, 
    applyAdvancedFilters, 
    serializeFilters, 
    deserializeFilters,
    getAvailableFilterFields,
    groupFieldsByCategory,
    groupFieldsBySections,
    OPERATORS 
} from '@/utils/filters/advancedFilterUtils';

/**
 * Hook for managing advanced filter state and operations
 * @param {object} options - Configuration options
 * @param {Array} options.customFields - Custom field definitions
 * @param {object} options.initialFilters - Initial filter state (serialized)
 * @param {Function} options.onFiltersChange - Callback when filters change
 * @returns {object} Filter state and operations
 */
const useAdvancedFilters = ({ customFields = [], initialFilters = null, onFiltersChange = () => {}, allowedFieldIds = null } = {}) => {
    const [conditions, setConditions] = useState(() => {
        if (initialFilters) {
            const deserialized = deserializeFilters(initialFilters);
            return deserialized.conditions;
        }
        return [];
    });
    
    const [logic, setLogic] = useState(() => {
        if (initialFilters) {
            const deserialized = deserializeFilters(initialFilters);
            return deserialized.logic;
        }
        return 'AND';
    });

    // Get all available filter fields (plan-gated when allowedFieldIds given)
    const availableFields = useMemo(() => {
        return getAvailableFilterFields(customFields, allowedFieldIds);
    }, [customFields, allowedFieldIds]);

    // Group fields by category for UI
    const groupedFields = useMemo(() => {
        return groupFieldsByCategory(availableFields);
    }, [availableFields]);

    // Group fields by section for sectioned UI (sidebar / dropdown)
    const sectionedFields = useMemo(() => {
        return groupFieldsBySections(availableFields);
    }, [availableFields]);

    // Get fields that haven't been used yet
    const unusedFields = useMemo(() => {
        const usedFieldIds = conditions.map(c => c.fieldId);
        return availableFields.filter(f => !usedFieldIds.includes(f.id));
    }, [availableFields, conditions]);

    // Add a new filter condition
    const addCondition = useCallback((field) => {
        const newCondition = createFilterCondition(field);
        setConditions(prev => {
            const updated = [...prev, newCondition];
            onFiltersChange(serializeFilters(updated, logic));
            return updated;
        });
        return newCondition;
    }, [logic, onFiltersChange]);

    // Remove a filter condition
    const removeCondition = useCallback((conditionId) => {
        setConditions(prev => {
            const updated = prev.filter(c => c.id !== conditionId);
            onFiltersChange(serializeFilters(updated, logic));
            return updated;
        });
    }, [logic, onFiltersChange]);

    // Update a filter condition
    const updateCondition = useCallback((conditionId, updates) => {
        setConditions(prev => {
            const updated = prev.map(c => {
                if (c.id !== conditionId) return c;
                
                const newCondition = { ...c, ...updates };
                
                // Reset value when operator changes to empty check
                if (updates.operator && ['is_empty', 'is_not_empty'].includes(updates.operator)) {
                    newCondition.value = '';
                    newCondition.value2 = '';
                }
                
                // Reset value2 when operator is not 'between'
                if (updates.operator && updates.operator !== 'between') {
                    newCondition.value2 = '';
                }
                
                return newCondition;
            });
            onFiltersChange(serializeFilters(updated, logic));
            return updated;
        });
    }, [logic, onFiltersChange]);

    // Update filter logic (AND/OR)
    const updateLogic = useCallback((newLogic) => {
        setLogic(newLogic);
        onFiltersChange(serializeFilters(conditions, newLogic));
    }, [conditions, onFiltersChange]);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setConditions([]);
        setLogic('AND');
        onFiltersChange(null);
    }, [onFiltersChange]);

    // Load filters from serialized state
    const loadFilters = useCallback((serialized) => {
        if (!serialized) {
            clearFilters();
            return;
        }
        const { conditions: loadedConditions, logic: loadedLogic } = deserializeFilters(serialized);
        setConditions(loadedConditions);
        setLogic(loadedLogic);
        onFiltersChange(serialized);
    }, [clearFilters, onFiltersChange]);

    // Apply filters to data
    const applyFilters = useCallback((data) => {
        return applyAdvancedFilters(data, conditions, logic);
    }, [conditions, logic]);

    // Get operators for a field type
    const getOperatorsForField = useCallback((fieldType) => {
        return OPERATORS[fieldType] || OPERATORS.text;
    }, []);

    // Check if filters are active
    const hasActiveFilters = conditions.length > 0;

    // Get serialized state for saving
    const getSerializedFilters = useCallback(() => {
        if (conditions.length === 0) return null;
        return serializeFilters(conditions, logic);
    }, [conditions, logic]);

    // Get active filter count
    const activeFilterCount = conditions.length;

    return {
        // State
        conditions,
        logic,
        availableFields,
        groupedFields,
        sectionedFields,
        unusedFields,
        hasActiveFilters,
        activeFilterCount,
        
        // Operations
        addCondition,
        removeCondition,
        updateCondition,
        updateLogic,
        clearFilters,
        loadFilters,
        applyFilters,
        getOperatorsForField,
        getSerializedFilters,
    };
};

export default useAdvancedFilters;
